import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Dialog,
  IconButton,
  Tabs,
  Tab,
  CircularProgress
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const MAX_VIDEOS = 4;

// Parses the public YouTube RSS feed (no API key required)
// Returns array of { id, title, date }
async function fetchLatestVideos(channelId) {
  const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
  // Use allorigins proxy to avoid CORS
  const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`;
  const res = await fetch(proxyUrl);
  if (!res.ok) throw new Error("RSS fetch failed");
  const data = await res.json();
  const xml = data.contents;
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "application/xml");
  const entries = Array.from(doc.querySelectorAll("entry"));
  return entries.slice(0, MAX_VIDEOS).map((entry) => {
    const videoId = entry.querySelector("videoId")?.textContent || "";
    const title = entry.querySelector("title")?.textContent || "";
    const published = entry.querySelector("published")?.textContent || "";
    const date = published
      ? new Date(published).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";
    return { id: videoId, title, date };
  });
}

export default function YouTubeSection({ config }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [latestVideos, setLatestVideos] = useState([]);
  const [latestLoading, setLatestLoading] = useState(true);
  const [latestError, setLatestError] = useState(false);

  // Extract channel ID from playlistId (UU... → UC...)
  const channelId = config?.playlistId?.startsWith("UU")
    ? "UC" + config.playlistId.slice(2)
    : config?.playlistId;

  useEffect(() => {
    if (!channelId) {
      setLatestLoading(false);
      return;
    }
    setLatestLoading(true);
    setLatestError(false);
    fetchLatestVideos(channelId)
      .then((vids) => {
        setLatestVideos(vids);
        setLatestLoading(false);
      })
      .catch(() => {
        setLatestError(true);
        setLatestLoading(false);
      });
  }, [channelId]);

  // Guard: must be after all hooks
  if (!config) return null;

  const { channelUrl, playlistId, videos } = config;

  // Popular videos: top 4 from the curated config list
  const popularVideos = (videos || []).slice(0, MAX_VIDEOS);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);
  const openVideo = (videoId) => setSelectedVideo(videoId);
  const closeVideo = () => setSelectedVideo(null);

  const VideoCard = ({ vid }) => (
    <Card
      sx={{
        height: "100%",
        background: "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow:
            "0 12px 30px rgba(0, 0, 0, 0.5), 0 0 15px rgba(212,175,55,0.2)",
          border: "1px solid rgba(212, 175, 55, 0.4)",
          "& .play-btn": {
            transform: "translate(-50%, -50%) scale(1.15)",
            backgroundColor: "#FF0000",
          },
          "& .overlay": {
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          position: "relative",
          cursor: "pointer",
          width: "100%",
          pt: "56.25%",
          overflow: "hidden",
        }}
        onClick={() => openVideo(vid.id)}
      >
        <CardMedia
          component="img"
          image={`https://img.youtube.com/vi/${vid.id}/hqdefault.jpg`}
          alt={vid.title}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
          }}
        />
        <Box
          className="overlay"
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            transition: "background-color 0.3s ease",
            zIndex: 2,
          }}
        />
        <Box
          className="play-btn"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            border: "2px solid #FFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            transition: "all 0.3s ease",
          }}
        >
          <PlayArrowIcon sx={{ color: "#FFF", fontSize: 32, ml: 0.3 }} />
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontFamily: "Quicksand",
            fontSize: "1.05rem",
            lineHeight: 1.4,
            color: "#FFF",
            mb: 2,
            height: "44px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            cursor: "pointer",
          }}
          onClick={() => openVideo(vid.id)}
        >
          {vid.title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: "auto",
            pt: 1.5,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontWeight: 500,
              fontFamily: "Varela Round",
            }}
          >
            {vid.date ? `Published: ${vid.date}` : ""}
          </Typography>

          <Button
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
            sx={{
              color: "#FFD700",
              fontFamily: "Quicksand",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.8rem",
              p: 0,
              minWidth: 0,
              "&:hover": {
                backgroundColor: "transparent",
                color: "#FFF",
              },
            }}
            onClick={() =>
              window.open(
                `https://www.youtube.com/watch?v=${vid.id}`,
                "_blank"
              )
            }
          >
            Watch on YT
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const ViewMoreButton = () => (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
      <Button
        variant="outlined"
        endIcon={<OpenInNewIcon />}
        onClick={() => window.open(channelUrl, "_blank")}
        sx={{
          borderColor: "rgba(255,215,0,0.6)",
          color: "#FFD700",
          fontFamily: "Quicksand",
          fontWeight: 700,
          fontSize: "1rem",
          px: 5,
          py: 1.4,
          borderRadius: "50px",
          borderWidth: "2px",
          textTransform: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "#FFD700",
            backgroundColor: "rgba(255,215,0,0.08)",
            transform: "translateY(-2px)",
            borderWidth: "2px",
          },
        }}
      >
        View More on YouTube
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        py: 8,
        px: { xs: 2, md: 6 },
        background: "linear-gradient(135deg, #120101 0%, #2A0404 100%)",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(191,6,3,0.15) 0%, rgba(0,0,0,0) 70%)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          right: "-10%",
          width: "450px",
          height: "450px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(0,0,0,0) 70%)",
          zIndex: 1,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            mb: 5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              backgroundColor: "rgba(191, 6, 3, 0.15)",
              border: "1px solid rgba(191, 6, 3, 0.4)",
              borderRadius: "50px",
              px: 2.5,
              py: 0.8,
            }}
          >
            <YouTubeIcon sx={{ color: "#FF0000", fontSize: 28 }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#FF5C5C",
                letterSpacing: 1.2,
                textTransform: "uppercase",
                fontFamily: "Quicksand",
              }}
            >
              Official YouTube Channel
            </Typography>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontFamily: "Quicksand",
              fontSize: { xs: "28px", md: "40px" },
              background: "linear-gradient(45deg, #FFF 30%, #FFD700 90%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
            }}
          >
            Watch &amp; Learn with Vivaha Sangamam
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: "700px",
              color: "rgba(255,255,255,0.7)",
              fontFamily: "Varela Round",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Stay updated with our latest Swayamvaram meetups, expert marriage
            consultation tips, horoscope guidance, and inspiring stories
            directly from our channel.
          </Typography>

          <Button
            variant="contained"
            startIcon={<YouTubeIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: "#FF0000",
              color: "#FFF",
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: "50px",
              fontFamily: "Quicksand",
              fontSize: "0.95rem",
              boxShadow: "0 4px 20px rgba(255, 0, 0, 0.4)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#CC0000",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 25px rgba(255, 0, 0, 0.6)",
              },
            }}
            onClick={() => window.open(channelUrl, "_blank")}
          >
            Subscribe Now
          </Button>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "rgba(255,255,255,0.1)",
            mb: 5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#FFD700",
                height: "3px",
                borderRadius: "3px",
              },
              "& .MuiTab-root": {
                fontFamily: "Quicksand",
                fontWeight: 700,
                fontSize: { xs: "0.9rem", sm: "1.05rem" },
                textTransform: "none",
                color: "rgba(255,255,255,0.6)",
                px: { xs: 2, sm: 4 },
                "&.Mui-selected": {
                  color: "#FFD700",
                },
              },
            }}
          >
            <Tab label="🆕 Latest Videos" id="yt-tab-latest" aria-controls="yt-panel-latest" />
            <Tab label="🔥 Most Popular" id="yt-tab-popular" aria-controls="yt-panel-popular" />
          </Tabs>
        </Box>

        {/* Tab Panel: Latest Videos */}
        {activeTab === 0 && (
          <Box id="yt-panel-latest" role="tabpanel">
            {latestLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#FFD700" }} />
              </Box>
            ) : latestError || latestVideos.length === 0 ? (
              // Fallback: show first 4 from config if RSS fails
              <>
                <Grid container spacing={4} justifyContent="center">
                  {(videos || []).slice(0, MAX_VIDEOS).map((vid) => (
                    <Grid item xs={12} sm={6} md={3} key={vid.id}>
                      <VideoCard vid={vid} />
                    </Grid>
                  ))}
                </Grid>
                <ViewMoreButton />
              </>
            ) : (
              <>
                <Grid container spacing={4} justifyContent="center">
                  {latestVideos.map((vid) => (
                    <Grid item xs={12} sm={6} md={3} key={vid.id}>
                      <VideoCard vid={vid} />
                    </Grid>
                  ))}
                </Grid>
                <ViewMoreButton />
              </>
            )}
          </Box>
        )}

        {/* Tab Panel: Most Popular */}
        {activeTab === 1 && (
          <Box id="yt-panel-popular" role="tabpanel">
            <Grid container spacing={4} justifyContent="center">
              {popularVideos.map((vid) => (
                <Grid item xs={12} sm={6} md={3} key={vid.id}>
                  <VideoCard vid={vid} />
                </Grid>
              ))}
            </Grid>
            <ViewMoreButton />
          </Box>
        )}
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={selectedVideo !== null}
        onClose={closeVideo}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(20, 2, 2, 0.95)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 24px 50px rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box sx={{ position: "relative", pt: "56.25%" }}>
          <IconButton
            onClick={closeVideo}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFF",
              zIndex: 10,
              "&:hover": { backgroundColor: "rgba(255,0,0,0.8)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo && (
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1&rel=0`}
              title="YouTube Video Player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
